import ExcelJS from "exceljs";
import ConstantsTexts from "../constants/ConstantsTexts";

class ExcelExportUtils {
  // Returns the list of static gray metadata columns used across comparison-mode styling
  static getGrayColumns() {
    return ["שם מסוף", "מק''ט", "רשות מקומית", "מחוז", "סוג", "סטטוס המסוף", "סטטוס תקציבי", "גורם מקדם"];
  }

  // Returns Excel number format constants for consistent formatting across the workbook
  static getExcelFormats() {
    return {
      WHOLE_NUMBER: "0",
      DECIMAL_ONE_PLACE: "0.0",
      DECIMAL_TWO_PLACES: "0.00",
      THOUSANDS_SEPARATOR: "#,##0",
      PERCENTAGE_TWO_PLACES: "0.00%",
      CURRENCY_SHEKEL: '"₪"#,##0',
      DATE_TIME: "dd/mm/yyyy hh:mm",
    };
  }

  // Returns column format definitions based on comparison mode for proper number formatting
  static getColumnFormats(isComparisonMode) {
    const EXCEL_FORMATS = this.getExcelFormats();

    return isComparisonMode
      ? {
          "מדד דחיפות": { numFmt: EXCEL_FORMATS.WHOLE_NUMBER },
          "מדד חשיבות": { numFmt: EXCEL_FORMATS.WHOLE_NUMBER },
          "מדד פוטנציאל": { numFmt: EXCEL_FORMATS.WHOLE_NUMBER },
          "מדד צמצום פער תחבורתי": { numFmt: EXCEL_FORMATS.WHOLE_NUMBER },
          "מדד עמדת הרשות המקומית": { numFmt: EXCEL_FORMATS.WHOLE_NUMBER },
          "מדד עלות לנסיעה": { numFmt: EXCEL_FORMATS.WHOLE_NUMBER },
          "מדד עלות לנוסע": { numFmt: EXCEL_FORMATS.WHOLE_NUMBER },
          ניקוד: { numFmt: EXCEL_FORMATS.WHOLE_NUMBER },
          דירוג: { numFmt: EXCEL_FORMATS.WHOLE_NUMBER },
        }
      : {
          "מק''ט": { numFmt: EXCEL_FORMATS.WHOLE_NUMBER },
          "מק''ט ארז": { numFmt: EXCEL_FORMATS.WHOLE_NUMBER },
          דירוג: { numFmt: EXCEL_FORMATS.WHOLE_NUMBER },
          "גודל מסוף נדרש (בדונם)": { numFmt: EXCEL_FORMATS.DECIMAL_ONE_PLACE },
          "תאריך עדכון אחרון": { numFmt: EXCEL_FORMATS.DATE_TIME },
        };
  }

  // Creates Excel workbook with worksheet configured for RTL, frozen panes, and zoom level
  static createExcelWorkbook(fileName, isComparisonMode) {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(fileName, {
      views: [
        {
          state: "frozen",
          ySplit: isComparisonMode ? 2 : 1,
          rightToLeft: true,
          zoomScale: isComparisonMode ? 100 : 85, // 85% zoom for non-comparison mode
        },
      ],
    });
    return { workbook, worksheet };
  }

  // Adds model version labels row with merged cells for comparison mode Excel export
  static addModelVersionLabels(worksheet, headers, selectedModelVersion, selectedModelVersion2) {
    // Add empty row first
    worksheet.addRow(new Array(headers.length).fill(""));

    // Now add merged cells for model labels
    let currentModel = null;
    let modelStartCol = 1;

    const grayColumns = this.getGrayColumns();

    headers.forEach((header, index) => {
      const displayHeader = header.endsWith("#2") ? header.slice(0, -2) : header;

      if (!grayColumns.includes(displayHeader)) {
        const headerModel = header.endsWith("#2") ? "model2" : "model1";

        if (currentModel !== headerModel) {
          // If we have a previous model, merge the cells
          if (currentModel && modelStartCol < index) {
            const modelLabel =
              currentModel === "model1"
                ? `${ConstantsTexts.CODE_119} ${selectedModelVersion?.modelVersionInt}`
                : `${ConstantsTexts.CODE_119} ${selectedModelVersion2?.modelVersionInt}`;
            worksheet.mergeCells(1, modelStartCol, 1, index);
            const cell = worksheet.getCell(1, modelStartCol);
            cell.value = modelLabel;
          }

          currentModel = headerModel;
          modelStartCol = index + 1;
        }
      }
    });

    // Handle the last model group
    if (currentModel && modelStartCol <= headers.length) {
      const modelLabel =
        currentModel === "model1" ? `${ConstantsTexts.CODE_119} ${selectedModelVersion?.modelVersionInt}` : `${ConstantsTexts.CODE_119} ${selectedModelVersion2?.modelVersionInt}`;
      worksheet.mergeCells(1, modelStartCol, 1, headers.length);
      const cell = worksheet.getCell(1, modelStartCol);
      cell.value = modelLabel;
    }
  }

  // Adds data rows to worksheet with appropriate number formatting for each column
  static addDataRows(worksheet, excelData, headers, columnFormats) {
    excelData.forEach((row) => {
      const dataRow = worksheet.addRow(headers.map((header) => row[header] ?? ""));

      // Apply number formatting to each cell based on column header
      dataRow.eachCell((cell, colNumber) => {
        const header = headers[colNumber - 1];
        const format = columnFormats[header]?.numFmt;
        if (format) {
          cell.numFmt = format;
        }
      });
    });
  }

  // Applies styling to model labels row with color coding for comparison mode
  static styleModelLabelsRow(worksheet, headers) {
    const modelLabelsRow = worksheet.getRow(1);
    const grayColumns = this.getGrayColumns();

    modelLabelsRow.eachCell((cell, colNumber) => {
      const header = headers[colNumber - 1];
      const displayHeader = header.endsWith("#2") ? header.slice(0, -2) : header;

      let headerColor;

      if (grayColumns.includes(displayHeader)) {
        headerColor = { argb: "FFFFFF" }; // Transparent (white) for gray columns in model labels row
      } else if (header.endsWith("#2")) {
        headerColor = { argb: "2E7D32" }; // Dark green
      } else {
        headerColor = { argb: "E97132" }; // Orange
      }

      cell.font = { bold: true, color: { argb: "FFFFFFFF" }, name: "Arial", size: 11 };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: headerColor };
      cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true, readingOrder: 2 };
      cell.border = { bottom: { style: "thin", color: { argb: "000000" } } }; // Thicker bottom border for model labels row
    });
  }

  // Applies styling to header row with color coding based on comparison mode and column type
  static styleHeaderRow(worksheet, headers, displayHeaders, isComparisonMode) {
    const headerRow = worksheet.getRow(isComparisonMode ? 2 : 1);

    // Set row height
    if (isComparisonMode) {
      headerRow.height = 30;
    } else {
      headerRow.height = 35;
    }

    headerRow.eachCell((cell, colNumber) => {
      const header = headers[colNumber - 1];
      const displayHeader = displayHeaders[colNumber - 1];

      // Define header colors and text colors
      let headerColor;
      let textColor;
      let borderColor;

      if (isComparisonMode) {
        // Gray for static metadata columns
        const grayColumns = this.getGrayColumns();
        // Green for Model 2 columns (originally had #2)
        const isModel2Column = header.endsWith("#2");
        // Orange for Model 1 columns (no #2)
        const isModel1Column = !header.endsWith("#2") && !grayColumns.includes(displayHeader);

        borderColor = { argb: "000000" };

        if (grayColumns.includes(displayHeader)) {
          headerColor = { argb: "CACACA" }; // Gray
          textColor = { argb: "FF000000" }; // Black text for gray columns
        } else if (isModel2Column) {
          headerColor = { argb: "2E7D32" }; // Dark green
          textColor = { argb: "FFFFFFFF" }; // White text for green columns
        } else if (isModel1Column) {
          headerColor = { argb: "E97132" }; // Orange
          textColor = { argb: "FFFFFFFF" }; // White text for orange columns
        } else {
          headerColor = { argb: "E97132" }; // Default to orange
          textColor = { argb: "FFFFFFFF" }; // White text
        }
      } else {
        headerColor = { argb: "E97132" }; // Default orange for non-comparison mode
        textColor = { argb: "000000" }; // Black text
        borderColor = { argb: "D45A1A" };
      }

      cell.font = { bold: true, color: textColor, name: "Arial", size: 11 };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: headerColor };
      cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true, readingOrder: 2 };
      cell.border = {
        top: { style: "thin", color: borderColor },
        bottom: { style: "thin", color: borderColor },
        left: { style: "thin", color: borderColor },
        right: { style: "thin", color: borderColor },
      };
    });
  }

  // Applies alternating row colors and column-specific styling to data rows
  static styleDataRows(worksheet, headers, isComparisonMode) {
    worksheet.eachRow((row, rowNumber) => {
      const headerRowNumber = isComparisonMode ? 2 : 1;

      if (rowNumber <= headerRowNumber) return; // skip header rows

      if (isComparisonMode) {
        // Custom styling for comparison mode
        row.eachCell((cell, colNumber) => {
          const header = headers[colNumber - 1];
          const displayHeader = header.endsWith("#2") ? header.slice(0, -2) : header;
          const grayColumns = this.getGrayColumns();

          cell.font = { name: "Arial", size: 11, color: { argb: "FF000000" } };

          // Align gray columns (static metadata) to right, except "מק''ט" which stays center
          const alignment = grayColumns.includes(displayHeader)
            ? displayHeader === "מק''ט"
              ? { horizontal: "center", vertical: "middle", readingOrder: 2 }
              : { horizontal: "right", vertical: "middle", readingOrder: 2 }
            : { horizontal: "center", vertical: "middle", readingOrder: 2 };

          cell.alignment = alignment;
          cell.border = {
            top: { style: "thin", color: { argb: "000000" } },
            bottom: { style: "thin", color: { argb: "000000" } },
            left: { style: "thin", color: { argb: "000000" } },
            right: { style: "thin", color: { argb: "000000" } },
          };

          // Set background colors based on column type and row alternation
          if (grayColumns.includes(displayHeader)) {
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFFF" } }; // Always white for gray columns
          } else if (header.endsWith("#2")) {
            // Light green for Model 2 columns on alternating rows (starting with color on row 3)
            if ((rowNumber - headerRowNumber) % 2 === 1) {
              cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "E8F5E8" } }; // Light green
            } else {
              cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFFF" } }; // White
            }
          } else {
            // Light orange for Model 1 columns on alternating rows (starting with color on row 3)
            if ((rowNumber - headerRowNumber) % 2 === 1) {
              cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FBE2D5" } }; // Light orange
            } else {
              cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFFF" } }; // White
            }
          }
        });
      } else {
        // Styling for non-comparison mode with bold specific columns
        row.eachCell((cell, colNumber) => {
          const header = headers[colNumber - 1];
          const boldColumns = ["מק''ט", "מק''ט ארז", "שם מסוף", "רשות מקומית"];

          // Make specific columns bold
          const isBold = boldColumns.includes(header);

          cell.font = {
            name: "Arial",
            size: 11,
            color: { argb: "FF000000" },
            bold: isBold,
          };
          cell.alignment = { horizontal: "center", vertical: "middle", readingOrder: 2 };
          cell.border = { bottom: { style: "thin", color: { argb: "D45A1A" } } };
        });
        // Alternate row color
        if ((rowNumber - headerRowNumber) % 2 === 0) {
          row.eachCell((cell) => {
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FBE2D5" } };
          });
        }
      }
    });
  }

  // Sets column widths based on content and adds autofilter functionality
  static configureWorksheet(worksheet, displayHeaders, excelData, headers, isComparisonMode) {
    if (isComparisonMode) {
      // For comparison mode, calculate width based on the widest cell content (rows 2+)
      worksheet.columns.forEach((col, colIndex) => {
        let maxWidth = displayHeaders[colIndex] ? displayHeaders[colIndex].length : 10;

        // Check all data rows (starting from row 3 in comparison mode)
        excelData.forEach((row) => {
          const cellValue = row[headers[colIndex]] ?? "";
          const cellString = String(cellValue);
          const cellWidth = cellString.length;
          if (cellWidth > maxWidth) {
            maxWidth = cellWidth;
          }
        });

        // Add some padding and set minimum width
        col.width = Math.max(maxWidth + 1);
      });
    } else {
      // For non-comparison mode, calculate width based on data rows only (not headers)
      worksheet.columns.forEach((col, colIndex) => {
        let maxWidth = 10; // Minimum width

        // Check all data rows only (starting from row 2 in non-comparison mode)
        excelData.forEach((row) => {
          const cellValue = row[headers[colIndex]] ?? "";
          const cellString = String(cellValue);
          const cellWidth = cellString.length;
          if (cellWidth > maxWidth) {
            maxWidth = cellWidth;
          }
        });

        // Add some padding and set width with maximum of 50px
        col.width = Math.min(Math.max(maxWidth + 2), 50);
      });
    }

    // Add autofilter to all columns
    worksheet.autoFilter = {
      from: { row: isComparisonMode ? 2 : 1, column: 1 },
      to: { row: isComparisonMode ? 2 : 1, column: displayHeaders.length },
    };
  }

  // Creates and downloads Excel file with proper MIME type and cleanup
  static async downloadExcelFile(workbook, fileName) {
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${fileName}.xlsx`;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  // Main Excel export function that orchestrates the complete process
  static async exportToExcel(excelData, isComparisonMode, selectedModelVersion, selectedModelVersion2, fileName) {
    if (!excelData || !excelData.length) {
      throw new Error("No data to export");
    }

    // Get headers from the first item in the response
    const headers = Object.keys(excelData[0]);

    // Create a new workbook and worksheet
    const { workbook, worksheet } = this.createExcelWorkbook(fileName, isComparisonMode);

    // Define column formats based on comparison mode
    const columnFormats = this.getColumnFormats(isComparisonMode);

    // Process headers for comparison mode - remove #2 suffix and create display headers
    const displayHeaders = isComparisonMode ? headers.map((header) => (header.endsWith("#2") ? header.slice(0, -2) : header)) : headers;

    // Add model version labels row for comparison mode
    if (isComparisonMode) {
      this.addModelVersionLabels(worksheet, headers, selectedModelVersion, selectedModelVersion2);
    }

    // Add header row
    worksheet.addRow(displayHeaders);

    // Add data rows with number formatting
    this.addDataRows(worksheet, excelData, headers, columnFormats);

    // Style model labels row for comparison mode
    if (isComparisonMode) {
      this.styleModelLabelsRow(worksheet, headers);
    }

    // Style header row with different colors based on comparison mode
    this.styleHeaderRow(worksheet, headers, displayHeaders, isComparisonMode);

    // Style data rows
    this.styleDataRows(worksheet, headers, isComparisonMode);

    // Set column widths and add autofilter
    this.configureWorksheet(worksheet, displayHeaders, excelData, headers, isComparisonMode);

    // Generate buffer and trigger download
    await this.downloadExcelFile(workbook, fileName);
  }
}

export default ExcelExportUtils;
